import { useEffect, useMemo, useState } from 'react'
import * as Sentry from '@sentry/react'
import { Check, ChevronDown, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  buildStandardApplicabilityDecisionPatches,
  type StandardApplicability,
  type TypeBQuestionnaireDefinition,
  type TypeBQuestionnaireExtractionEvidenceEntry,
  type TypeBQuestionnaireQuestion,
  type TypeBQuestionnaireSection,
  useBatchUpdateStandardApplicabilityDecisions,
  useGenerateTypeBQuestionnaireCandidates,
  useGetTypeBQuestionnaire,
  usePatchTypeBQuestionnaireAnswers,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Textarea } from '@/components/ui/textarea'
import {
  getExtractionEvidenceForQuestion,
  hasExtractionEvidenceContent,
  isQuestionAnswerBlank,
  shouldShowInsufficientEvidenceHint,
} from '../utils/typeBQuestionnaireExtractionEvidence'
import {
  buildDefaultSectionOpenByKey,
  countIncompleteVisibleQuestions,
  getActiveTypeBQuestionnaireSections,
  getSectionCompletionStats,
  getSectionIncompleteQuestionKeys,
  getSectionQuestions,
  isQuestionnaireComplete,
  SectionCompletionStats,
} from '../utils/typeBQuestionnaireWizard'
import { OnboardingStandardsSelectionTable } from './OnboardingStandardsSelectionTable'
import {
  TypeBQuestionnaireExtractionEvidenceBlock,
  TypeBQuestionnaireInsufficientEvidenceHint,
} from './TypeBQuestionnaireExtractionEvidenceDisplay'

type TypeBQuestionnaireStepProps = {
  complianceId: string
  standards: StandardApplicability[]
  onComplete: () => void
}

function isTypeBCandidateStandard(standard: StandardApplicability): boolean {
  return (
    standard.source === 'QUESTIONNAIRE_RULES' &&
    standard.type.trim().toUpperCase() === 'B'
  )
}

function candidateSort(
  first: StandardApplicability,
  second: StandardApplicability
): number {
  return first.reference.localeCompare(second.reference)
}

type TypeBQuestionnaireQuestionFieldsProps = {
  definition: TypeBQuestionnaireDefinition
  question: TypeBQuestionnaireQuestion
  value: boolean | string | null
  textDraftByQuestionKey: Record<string, string>
  extractionEvidence: Record<
    string,
    TypeBQuestionnaireExtractionEvidenceEntry | null
  >
  dismissedInsufficientHintKeys: Set<string>
  isQuestionnaireBusy: boolean
  isQuestionActive: boolean
  isTextInputDisabled: boolean
  showValidationErrors: boolean
  isMissingRequiredAnswer: boolean
  onAnswer: (questionKey: string, value: unknown) => void
  onTextDraftChange: (questionKey: string, value: string) => void
  onTextAnswerBlur: (questionKey: string) => void
  translate: (key: string, options?: Record<string, unknown>) => string
}

function TypeBQuestionnaireQuestionEvidence({
  question,
  value,
  textDraftByQuestionKey,
  extractionEvidence,
  dismissedInsufficientHintKeys,
  isQuestionActive,
  translate,
}: {
  question: TypeBQuestionnaireQuestion
  value: boolean | string | null
  textDraftByQuestionKey: Record<string, string>
  extractionEvidence: Record<
    string,
    TypeBQuestionnaireExtractionEvidenceEntry | null
  >
  dismissedInsufficientHintKeys: Set<string>
  isQuestionActive: boolean
  translate: (key: string, options?: Record<string, unknown>) => string
}) {
  const evidenceEntry = getExtractionEvidenceForQuestion(
    extractionEvidence,
    question.questionKey
  )
  if (!isQuestionActive) {
    return null
  }
  const isAnswerBlank = isQuestionAnswerBlank({
    question,
    value,
    textDraft: textDraftByQuestionKey[question.questionKey],
  })
  const showInsufficientHint = shouldShowInsufficientEvidenceHint({
    questionKey: question.questionKey,
    extractionEvidence,
    isQuestionActive,
    isAnswerBlank,
    isHintDismissed: dismissedInsufficientHintKeys.has(question.questionKey),
  })

  if (hasExtractionEvidenceContent(evidenceEntry)) {
    return (
      <TypeBQuestionnaireExtractionEvidenceBlock
        evidence={evidenceEntry}
        translate={translate}
      />
    )
  }

  if (showInsufficientHint) {
    return <TypeBQuestionnaireInsufficientEvidenceHint translate={translate} />
  }

  return null
}

function TypeBQuestionnaireQuestionFields({
  definition,
  question,
  value,
  textDraftByQuestionKey,
  extractionEvidence,
  dismissedInsufficientHintKeys,
  isQuestionnaireBusy,
  isQuestionActive,
  isTextInputDisabled,
  showValidationErrors,
  isMissingRequiredAnswer,
  onAnswer,
  onTextDraftChange,
  onTextAnswerBlur,
  translate,
}: TypeBQuestionnaireQuestionFieldsProps) {
  const isFieldDisabled = isQuestionnaireBusy || !isQuestionActive
  const isQuestionInactive = !isQuestionActive
  const shouldShowValidationError =
    showValidationErrors && isQuestionActive && isMissingRequiredAnswer
  const hasHelperText =
    question.helperKey != null && question.helperKey.length > 0
  const helperTextKey = question.helperKey
  const isYesSelected = value === true
  const isNoSelected = value === false
  const isMachineMobilityQuestion =
    question.kind === 'single_select' &&
    question.questionKey === 'machine_mobility'
  const isTextInputFieldDisabled = isTextInputDisabled || isQuestionInactive

  if (question.kind === 'boolean') {
    return (
      <div
        className={cn(
          'space-y-3 border-b pb-6 last:border-b-0 last:pb-0',
          isQuestionInactive && 'opacity-50'
        )}
      >
        <div className='flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-6'>
          <div className='min-w-0 flex-1 space-y-1'>
            <div className='flex flex-wrap items-center gap-2'>
              <Label
                className={cn(
                  isMissingRequiredAnswer ? 'text-destructive' : undefined,
                  isQuestionInactive && 'text-muted-foreground'
                )}
              >
                {translate(question.labelKey)}
              </Label>
              {shouldShowValidationError ? (
                <span className='text-xs font-medium text-destructive'>
                  {translate('onboarding.typeBQuestionnaire.answerRequired', {
                    defaultValue: 'Please answer',
                  })}
                </span>
              ) : null}
            </div>
            {hasHelperText && helperTextKey != null && (
              <p className='text-sm text-muted-foreground'>
                {translate(helperTextKey)}
              </p>
            )}
          </div>
          <div className='flex shrink-0 gap-2'>
            <Button
              type='button'
              size='sm'
              variant={isYesSelected ? 'default' : 'outline'}
              disabled={isFieldDisabled}
              aria-pressed={isYesSelected}
              aria-disabled={isQuestionInactive}
              onClick={() => {
                if (isFieldDisabled) {
                  return
                }
                onAnswer(question.questionKey, true)
              }}
            >
              {translate('onboarding.machineCompletion.yes')}
            </Button>
            <Button
              type='button'
              size='sm'
              variant={isNoSelected ? 'default' : 'outline'}
              disabled={isFieldDisabled}
              aria-pressed={isNoSelected}
              aria-disabled={isQuestionInactive}
              onClick={() => {
                if (isFieldDisabled) {
                  return
                }
                onAnswer(question.questionKey, false)
              }}
            >
              {translate('onboarding.machineCompletion.no')}
            </Button>
          </div>
        </div>
        <TypeBQuestionnaireQuestionEvidence
          question={question}
          value={value}
          textDraftByQuestionKey={textDraftByQuestionKey}
          extractionEvidence={extractionEvidence}
          dismissedInsufficientHintKeys={dismissedInsufficientHintKeys}
          isQuestionActive={isQuestionActive}
          translate={translate}
        />
      </div>
    )
  }

  return (
    <div
      className={cn(
        'space-y-3 border-b pb-6 last:border-b-0 last:pb-0',
        isQuestionInactive && 'opacity-50'
      )}
    >
      <div className='flex flex-wrap items-center gap-2'>
        <Label
          className={cn(
            isMissingRequiredAnswer ? 'text-destructive' : undefined,
            isQuestionInactive && 'text-muted-foreground'
          )}
        >
          {translate(question.labelKey)}
        </Label>
        {shouldShowValidationError ? (
          <span className='text-xs font-medium text-destructive'>
            {translate('onboarding.typeBQuestionnaire.answerRequired', {
              defaultValue: 'Please answer',
            })}
          </span>
        ) : null}
      </div>
      {hasHelperText && helperTextKey != null && (
        <p className='text-xs text-muted-foreground'>
          {translate(helperTextKey)}
        </p>
      )}
      {isMachineMobilityQuestion && (
        <RadioGroup
          value={typeof value === 'string' ? value : ''}
          onValueChange={(next) => {
            if (isFieldDisabled) {
              return
            }
            onAnswer(question.questionKey, next)
          }}
          className='flex flex-col gap-2'
        >
          {definition.enums.machine_mobility.map((option) => (
            <div key={option} className='flex items-center gap-2'>
              <RadioGroupItem
                value={option}
                id={`${question.questionKey}-${option}`}
                disabled={isFieldDisabled}
              />
              <Label htmlFor={`${question.questionKey}-${option}`}>
                {translate(
                  `onboarding.typeBQuestionnaire.options.machine_mobility.${option}`
                )}
              </Label>
            </div>
          ))}
        </RadioGroup>
      )}
      {question.kind === 'text' && (
        <Textarea
          value={textDraftByQuestionKey[question.questionKey] ?? ''}
          onChange={(event) => {
            onTextDraftChange(question.questionKey, event.target.value)
          }}
          onBlur={() => {
            onTextAnswerBlur(question.questionKey)
          }}
          rows={4}
          disabled={isTextInputFieldDisabled}
        />
      )}
      <TypeBQuestionnaireQuestionEvidence
        question={question}
        value={value}
        textDraftByQuestionKey={textDraftByQuestionKey}
        extractionEvidence={extractionEvidence}
        dismissedInsufficientHintKeys={dismissedInsufficientHintKeys}
        isQuestionActive={isQuestionActive}
        translate={translate}
      />
    </div>
  )
}

type TypeBQuestionnaireSectionCardProps = {
  section: TypeBQuestionnaireSection
  definition: TypeBQuestionnaireDefinition
  visibleKeySet: Set<string>
  effectiveAnswers: Record<string, boolean | string | null>
  textDraftByQuestionKey: Record<string, string>
  extractionEvidence: Record<
    string,
    TypeBQuestionnaireExtractionEvidenceEntry | null
  >
  dismissedInsufficientHintKeys: Set<string>
  isOpen: boolean | undefined
  showValidationErrors: boolean
  isQuestionnaireBusy: boolean
  isTextInputDisabled: boolean
  onOpenChange: (open: boolean) => void
  onAnswer: (questionKey: string, value: unknown) => void
  onTextDraftChange: (questionKey: string, value: string) => void
  onTextAnswerBlur: (questionKey: string) => void
  translate: (key: string, options?: Record<string, unknown>) => string
}

function CompletionStatsDisplay({
  completionStats,
}: {
  completionStats: SectionCompletionStats
}) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  if (completionStats.isComplete) {
    return (
      <span className='inline-flex items-center gap-1 text-sm text-muted-foreground'>
        <Check className='size-4 shrink-0 text-emerald-600' aria-hidden />
        {t('onboarding.typeBQuestionnaire.sectionComplete')}
      </span>
    )
  }
  return (
    <span className='text-sm text-muted-foreground'>
      {t('onboarding.typeBQuestionnaire.sectionAnsweredCount', {
        answered: completionStats.answeredRequiredVisible,
        total: completionStats.totalRequiredVisible,
      })}
    </span>
  )
}

export function TypeBQuestionnaireSectionCard({
  section,
  definition,
  visibleKeySet,
  effectiveAnswers,
  textDraftByQuestionKey,
  extractionEvidence,
  dismissedInsufficientHintKeys,
  isOpen,
  showValidationErrors,
  isQuestionnaireBusy,
  isTextInputDisabled,
  onOpenChange,
  onAnswer,
  onTextDraftChange,
  onTextAnswerBlur,
  translate,
}: TypeBQuestionnaireSectionCardProps) {
  const sectionQuestions = useMemo(() => {
    return getSectionQuestions({
      definition,
      sectionKey: section.sectionKey,
    })
  }, [definition, section.sectionKey])

  const completionStats = useMemo(() => {
    return getSectionCompletionStats({
      sectionQuestions,
      visibleQuestionKeys: visibleKeySet,
      answers: effectiveAnswers,
      textDraftByQuestionKey,
    })
  }, [
    sectionQuestions,
    visibleKeySet,
    effectiveAnswers,
    textDraftByQuestionKey,
  ])

  const resolvedOpen = isOpen ?? !completionStats.isComplete

  return (
    <Collapsible
      open={resolvedOpen}
      onOpenChange={onOpenChange}
      className='group/section'
    >
      <Card>
        <CardHeader className='pb-3'>
          <CollapsibleTrigger
            render={
              <button
                type='button'
                className='flex w-full items-center gap-3 text-start'
              />
            }
          >
            <div className='flex min-w-0 flex-1 justify-between'>
              <CardTitle className='text-base'>
                {translate(section.titleKey)}
              </CardTitle>
              <CompletionStatsDisplay completionStats={completionStats} />
            </div>
            <ChevronDown
              className='mt-0.5 size-5 shrink-0 text-muted-foreground transition-transform duration-200 in-data-panel-open:rotate-180'
              aria-hidden
            />
          </CollapsibleTrigger>
        </CardHeader>
        <CollapsibleContent>
          <CardContent className='space-y-6 pt-0'>
            <p className='text-sm text-muted-foreground'>
              {translate(section.introKey)}
            </p>
            {sectionQuestions.map((question) => {
              const value = effectiveAnswers[question.questionKey] ?? null
              const isQuestionActive = visibleKeySet.has(question.questionKey)
              const isQuestionIncomplete =
                getSectionIncompleteQuestionKeys({
                  sectionQuestions: [question],
                  visibleQuestionKeys: visibleKeySet,
                  answers: effectiveAnswers,
                  textDraftByQuestionKey,
                }).length > 0
              const isMissingRequiredAnswer =
                isQuestionActive && showValidationErrors && isQuestionIncomplete
              return (
                <TypeBQuestionnaireQuestionFields
                  key={question.questionKey}
                  definition={definition}
                  question={question}
                  value={value}
                  textDraftByQuestionKey={textDraftByQuestionKey}
                  extractionEvidence={extractionEvidence}
                  dismissedInsufficientHintKeys={dismissedInsufficientHintKeys}
                  isQuestionnaireBusy={isQuestionnaireBusy}
                  isQuestionActive={isQuestionActive}
                  isTextInputDisabled={isTextInputDisabled}
                  showValidationErrors={showValidationErrors}
                  isMissingRequiredAnswer={isMissingRequiredAnswer}
                  onAnswer={onAnswer}
                  onTextDraftChange={onTextDraftChange}
                  onTextAnswerBlur={onTextAnswerBlur}
                  translate={translate}
                />
              )
            })}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}

export function TypeBQuestionnaireStep({
  complianceId,
  standards,
  onComplete,
}: TypeBQuestionnaireStepProps) {
  const { t } = useTranslation(['machineryDirectiveCompliance', 'common'])
  const questionnaireQuery = useGetTypeBQuestionnaire(complianceId)
  const patchAnswersMutation = usePatchTypeBQuestionnaireAnswers(complianceId)
  const generateMutation = useGenerateTypeBQuestionnaireCandidates(complianceId)
  const batchMutation =
    useBatchUpdateStandardApplicabilityDecisions(complianceId)

  const reportAndToastMutationError = (
    error: Error,
    localizedMessageKey:
      | 'onboarding.typeBQuestionnaire.saveAnswerFailed'
      | 'onboarding.typeBQuestionnaire.generateFailed'
      | 'onboarding.typeBCandidates.saveSelectionFailed',
    action: 'save_answer' | 'generate_candidates' | 'save_candidate_selection'
  ) => {
    Sentry.captureException(error, {
      tags: {
        area: 'machinery-directive-onboarding',
        step: 'type-b-questionnaire',
        action,
      },
      extra: {
        complianceId,
      },
    })
    toast.error(t(localizedMessageKey))
  }

  const questionnaireState = questionnaireQuery.data
  const definition = questionnaireState?.definition
  const answers = questionnaireState?.answers ?? {}
  const extractionEvidence = questionnaireState?.extractionEvidence ?? {}
  const completion = questionnaireState?.completion

  const visibleQuestionKeys = completion?.visibleQuestionKeys ?? []
  const visibleKeySet = useMemo(
    () => new Set(visibleQuestionKeys),
    [visibleQuestionKeys]
  )

  const typeBCandidates = useMemo(() => {
    return standards.filter(isTypeBCandidateStandard).sort(candidateSort)
  }, [standards])
  const hasGeneratedCandidates = questionnaireState?.isGenerated === true
  const [isEditingQuestionnaire, setIsEditingQuestionnaire] = useState(false)
  const showCandidatesReview = hasGeneratedCandidates && !isEditingQuestionnaire
  const hasTypeBCandidates = typeBCandidates.length > 0
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () =>
      new Set(
        typeBCandidates
          .filter((standard) => standard.isAccepted === true)
          .map((standard) => standard.id)
      )
  )

  const isSavingAnswer = patchAnswersMutation.isPending
  const isQuestionnaireBusy =
    isSavingAnswer || generateMutation.isPending || questionnaireQuery.isLoading
  const isGenerateBusy =
    generateMutation.isPending || questionnaireQuery.isLoading
  const isTextInputDisabled =
    generateMutation.isPending || questionnaireQuery.isLoading
  const [textDraftByQuestionKey, setTextDraftByQuestionKey] = useState<
    Record<string, string>
  >({})
  const [optimisticAnswers, setOptimisticAnswers] = useState<
    Record<string, boolean | string | null>
  >({})
  const [showValidationErrors, setShowValidationErrors] = useState(false)
  const [sectionOpenByKey, setSectionOpenByKey] = useState<
    Record<string, boolean>
  >({})
  const [questionnaireVisitKey, setQuestionnaireVisitKey] = useState(0)
  const [dismissedInsufficientHintKeys, setDismissedInsufficientHintKeys] =
    useState<Set<string>>(() => new Set())

  const dismissInsufficientEvidenceHint = (questionKey: string) => {
    setDismissedInsufficientHintKeys((previous) => {
      if (previous.has(questionKey)) {
        return previous
      }
      const next = new Set(previous)
      next.add(questionKey)
      return next
    })
  }

  useEffect(() => {
    if (!hasGeneratedCandidates) {
      setIsEditingQuestionnaire(false)
    }
  }, [hasGeneratedCandidates])

  useEffect(() => {
    setSelectedIds(
      new Set(
        typeBCandidates
          .filter((standard) => standard.isAccepted === true)
          .map((standard) => standard.id)
      )
    )
  }, [typeBCandidates])

  useEffect(() => {
    if (!definition) {
      return
    }
    setTextDraftByQuestionKey((previous) => {
      let hasChanges = false
      const next = { ...previous }
      for (const question of definition.questions) {
        if (question.kind !== 'text') {
          continue
        }
        const persistedValue =
          typeof answers[question.questionKey] === 'string'
            ? String(answers[question.questionKey])
            : ''
        if (
          !(question.questionKey in next) ||
          next[question.questionKey] !== persistedValue
        ) {
          next[question.questionKey] = persistedValue
          hasChanges = true
        }
      }
      if (!hasChanges) {
        return previous
      }
      return next
    })
  }, [definition, answers])

  useEffect(() => {
    setOptimisticAnswers((previous) => {
      if (Object.keys(previous).length === 0) {
        return previous
      }
      const next = { ...previous }
      let hasChanges = false
      for (const questionKey of Object.keys(previous)) {
        if (answers[questionKey] === previous[questionKey]) {
          delete next[questionKey]
          hasChanges = true
        }
      }
      if (!hasChanges) {
        return previous
      }
      return next
    })
  }, [answers])

  const effectiveAnswers = useMemo(() => {
    return { ...answers, ...optimisticAnswers }
  }, [answers, optimisticAnswers])

  const activeSections = useMemo(() => {
    if (!definition) {
      return []
    }
    return getActiveTypeBQuestionnaireSections({
      definition,
      visibleQuestionKeys: visibleKeySet,
    })
  }, [definition, visibleKeySet])

  const wizardSections = activeSections

  useEffect(() => {
    if (!definition || activeSections.length === 0) {
      return
    }
    setSectionOpenByKey(
      buildDefaultSectionOpenByKey({
        activeSections,
        definition,
        visibleQuestionKeys: visibleKeySet,
        answers: effectiveAnswers,
        textDraftByQuestionKey,
      })
    )
  }, [questionnaireVisitKey, definition])

  const remainingQuestionCount = useMemo(() => {
    if (!definition) {
      return 0
    }
    return countIncompleteVisibleQuestions({
      activeSections,
      definition,
      visibleQuestionKeys: visibleKeySet,
      answers: effectiveAnswers,
      textDraftByQuestionKey,
    })
  }, [
    activeSections,
    definition,
    effectiveAnswers,
    textDraftByQuestionKey,
    visibleKeySet,
  ])

  const isQuestionnaireCompleteLocally = useMemo(() => {
    if (!definition) {
      return false
    }
    return isQuestionnaireComplete({
      wizardSections,
      definition,
      visibleQuestionKeys: visibleKeySet,
      answers: effectiveAnswers,
      textDraftByQuestionKey,
    })
  }, [
    wizardSections,
    definition,
    effectiveAnswers,
    textDraftByQuestionKey,
    visibleKeySet,
  ])

  const isGeneratingCandidates = generateMutation.isPending
  const canReturnToCandidateReview =
    isEditingQuestionnaire &&
    hasGeneratedCandidates &&
    isQuestionnaireCompleteLocally
  const generateButtonDisabledTooltip = !isQuestionnaireCompleteLocally
    ? t('onboarding.typeBQuestionnaire.generateDisabledTooltip')
    : undefined

  const expandIncompleteSections = () => {
    if (!definition) {
      return
    }
    setSectionOpenByKey((previous) => {
      const next = { ...previous }
      for (const section of activeSections) {
        const sectionQuestions = getSectionQuestions({
          definition,
          sectionKey: section.sectionKey,
        })
        const stats = getSectionCompletionStats({
          sectionQuestions,
          visibleQuestionKeys: visibleKeySet,
          answers: effectiveAnswers,
          textDraftByQuestionKey,
        })
        if (!stats.isComplete) {
          next[section.sectionKey] = true
        }
      }
      return next
    })
  }

  const handleAnswer = (questionKey: string, value: unknown) => {
    dismissInsufficientEvidenceHint(questionKey)
    let nextValue: boolean | string | null = null
    if (typeof value === 'boolean' || typeof value === 'string') {
      nextValue = value
    }
    setOptimisticAnswers((previous) => ({
      ...previous,
      [questionKey]: nextValue,
    }))
    patchAnswersMutation.mutate(
      {
        answers: {
          [questionKey]: value,
        },
      },
      {
        onError: (error) => {
          reportAndToastMutationError(
            error,
            'onboarding.typeBQuestionnaire.saveAnswerFailed',
            'save_answer'
          )
        },
      }
    )
  }

  const handleTextDraftChange = (questionKey: string, value: string) => {
    dismissInsufficientEvidenceHint(questionKey)
    setTextDraftByQuestionKey((previous) => ({
      ...previous,
      [questionKey]: value,
    }))
  }

  const handleTextAnswerBlur = (questionKey: string) => {
    const draft = textDraftByQuestionKey[questionKey] ?? ''
    const persisted =
      typeof answers[questionKey] === 'string'
        ? String(answers[questionKey])
        : ''
    if (draft === persisted) {
      return
    }
    handleAnswer(questionKey, draft)
  }

  const handleGenerate = () => {
    if (!isQuestionnaireCompleteLocally) {
      setShowValidationErrors(true)
      expandIncompleteSections()
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    generateMutation.mutate(undefined, {
      onSuccess: () => {
        setIsEditingQuestionnaire(false)
        setShowValidationErrors(false)
        window.scrollTo({ top: 0, behavior: 'smooth' })
      },
      onError: (error) => {
        reportAndToastMutationError(
          error,
          'onboarding.typeBQuestionnaire.generateFailed',
          'generate_candidates'
        )
      },
    })
  }

  const handleBackToCandidateReview = () => {
    setIsEditingQuestionnaire(false)
    setShowValidationErrors(false)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleEditQuestionnaire = () => {
    setIsEditingQuestionnaire(true)
    setShowValidationErrors(false)
    setQuestionnaireVisitKey((previous) => previous + 1)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleSectionOpenChange = (sectionKey: string, open: boolean) => {
    setSectionOpenByKey((previous) => ({
      ...previous,
      [sectionKey]: open,
    }))
  }

  const handleCandidateToggle = (standardId: string, checked: boolean) => {
    setSelectedIds((previous) => {
      const next = new Set(previous)
      if (checked) {
        next.add(standardId)
      } else {
        next.delete(standardId)
      }
      return next
    })
  }

  const handleCandidateToggleAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(typeBCandidates.map((standard) => standard.id)))
      return
    }
    setSelectedIds(new Set())
  }

  const handleCandidateContinue = () => {
    const patches = buildStandardApplicabilityDecisionPatches(
      typeBCandidates,
      selectedIds
    )
    batchMutation.mutate(patches, {
      onSuccess: () => {
        onComplete()
      },
      onError: (error) => {
        reportAndToastMutationError(
          error,
          'onboarding.typeBCandidates.saveSelectionFailed',
          'save_candidate_selection'
        )
      },
    })
  }

  const isQuestionnaireLoadError = questionnaireQuery.isError
  if (isQuestionnaireLoadError) {
    return (
      <div className='space-y-6'>
        <p className='leading-relaxed text-muted-foreground'>
          {t('onboarding.typeBQuestionnaire.loadFailed')}
        </p>
        <div className='flex justify-end'>
          <Button
            onClick={() => {
              void questionnaireQuery.refetch()
            }}
            disabled={questionnaireQuery.isFetching}
          >
            {questionnaireQuery.isFetching && (
              <Loader2 className='mr-2 size-4 animate-spin' />
            )}
            {t('onboarding.typeBQuestionnaire.retryLoadButton')}
          </Button>
        </div>
      </div>
    )
  }

  const isQuestionnaireLoading =
    questionnaireQuery.isLoading || definition == null || completion == null
  if (isQuestionnaireLoading) {
    return (
      <div className='flex flex-1 items-center justify-center'>
        <Loader2 className='size-8 animate-spin text-muted-foreground' />
      </div>
    )
  }

  if (showCandidatesReview) {
    return (
      <div className='flex min-h-0 flex-1 flex-col'>
        <div className='flex min-h-0 flex-1 flex-col gap-6'>
          <div className='space-y-4'>
            <h2 className='text-lg font-semibold tracking-tight'>
              {t('onboarding.typeBCandidates.title')}
            </h2>
            <p className='leading-relaxed text-muted-foreground'>
              {t('onboarding.typeBCandidates.description')}
            </p>
          </div>
          {hasTypeBCandidates ? (
            <OnboardingStandardsSelectionTable
              standards={typeBCandidates}
              selectedIds={selectedIds}
              disabled={batchMutation.isPending}
              onToggle={handleCandidateToggle}
              onToggleAll={handleCandidateToggleAll}
            />
          ) : (
            <p className='text-sm text-muted-foreground'>
              {t('onboarding.typeBCandidates.noCandidates')}
            </p>
          )}
        </div>
        <div className='mt-6 flex shrink-0 flex-wrap items-center justify-between gap-4 border-t pt-6'>
          <Button
            type='button'
            variant='outline'
            onClick={handleEditQuestionnaire}
            disabled={batchMutation.isPending}
          >
            {t('onboarding.typeBCandidates.editQuestionnaireButton')}
          </Button>
          <Button
            onClick={handleCandidateContinue}
            disabled={batchMutation.isPending}
          >
            {batchMutation.isPending && (
              <Loader2 className='mr-2 size-4 animate-spin' />
            )}
            {t('onboarding.typeBCandidates.continueButton')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className='flex min-h-0 flex-1 flex-col'>
      <div className='flex min-h-0 flex-1 flex-col gap-6'>
        <div className='space-y-4'>
          <h2 className='text-lg font-semibold tracking-tight'>
            {t('onboarding.typeBQuestionnaire.title')}
          </h2>
          <p className='leading-relaxed text-muted-foreground'>
            {t('onboarding.typeBQuestionnaire.intro')}
          </p>
        </div>

        <div className='flex flex-col gap-4'>
          {activeSections.map((section) => (
            <TypeBQuestionnaireSectionCard
              key={section.sectionKey}
              section={section}
              definition={definition}
              visibleKeySet={visibleKeySet}
              effectiveAnswers={effectiveAnswers}
              textDraftByQuestionKey={textDraftByQuestionKey}
              extractionEvidence={extractionEvidence}
              dismissedInsufficientHintKeys={dismissedInsufficientHintKeys}
              isOpen={sectionOpenByKey[section.sectionKey]}
              showValidationErrors={showValidationErrors}
              isQuestionnaireBusy={isQuestionnaireBusy}
              isTextInputDisabled={isTextInputDisabled}
              onOpenChange={(open) => {
                handleSectionOpenChange(section.sectionKey, open)
              }}
              onAnswer={handleAnswer}
              onTextDraftChange={handleTextDraftChange}
              onTextAnswerBlur={handleTextAnswerBlur}
              translate={t}
            />
          ))}
        </div>
      </div>

      <div className='mt-6 shrink-0 border-t bg-background/95 pt-6 backdrop-blur supports-backdrop-filter:bg-background/80'>
        <div className='flex flex-wrap items-center justify-between gap-4'>
          <div className='space-y-1'>
            {remainingQuestionCount > 0 ? (
              <p className='text-sm text-muted-foreground'>
                {t('onboarding.typeBQuestionnaire.questionsRemaining', {
                  count: remainingQuestionCount,
                })}
              </p>
            ) : (
              <p className='text-sm text-muted-foreground'>
                {t('onboarding.typeBQuestionnaire.readyToGenerate')}
              </p>
            )}
            {showValidationErrors && remainingQuestionCount > 0 ? (
              <p className='text-sm font-medium text-destructive'>
                {t('onboarding.typeBQuestionnaire.completeQuestionnaireHint')}
              </p>
            ) : null}
          </div>
          <div className='flex w-full items-center justify-between gap-2'>
            {canReturnToCandidateReview ? (
              <Button
                type='button'
                variant='outline'
                onClick={handleBackToCandidateReview}
                disabled={isGenerateBusy}
              >
                {t('onboarding.typeBQuestionnaire.backToCandidateReviewButton')}
              </Button>
            ) : null}
            <div className='flex w-full items-center justify-end gap-2'>
              <Button
                onClick={handleGenerate}
                disabled={isGenerateBusy}
                title={generateButtonDisabledTooltip}
              >
                {isGeneratingCandidates && (
                  <Loader2 className='mr-2 size-4 animate-spin' />
                )}
                {t('onboarding.typeBQuestionnaire.generateButton')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
